import { useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';

/**
 * useBotDefense - Invisible Bot Defense Hook
 * Supports Cloudflare Turnstile & Google reCAPTCHA v3 (Invisible Mode)
 * Ensures smooth, frictionless protection with zero user puzzles.
 */
export const useBotDefense = () => {
  const { siteSettings } = useLanguage();
  const botConfig = siteSettings?.bot_defense || null;

  const enabled = !!botConfig?.enabled;
  const provider = botConfig?.provider || 'turnstile';
  const siteKey = (botConfig?.site_key || '').trim();

  const isScriptLoadedRef = useRef(false);
  const turnstileWidgetIdRef = useRef(null);

  // Dynamically load provider script
  useEffect(() => {
    if (!enabled || !siteKey) return;

    const scriptId = provider === 'turnstile' ? 'cf-turnstile-script' : 'recaptcha-v3-script';
    const existingScript = document.getElementById(scriptId);

    if (existingScript) {
      isScriptLoadedRef.current = true;
      return;
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.defer = true;

    if (provider === 'turnstile') {
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    } else {
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    }

    script.onload = () => {
      isScriptLoadedRef.current = true;
    };

    script.onerror = () => {
      // If script fails (e.g. adblocker), graceful fallback to honeypot
      isScriptLoadedRef.current = false;
    };

    document.head.appendChild(script);

    return () => {
      // Keep script cached in document head for performance
    };
  }, [enabled, provider, siteKey]);

  // Execute invisible check and retrieve token
  const executeBotDefense = useCallback(async (actionName = 'submit') => {
    // If not enabled or no public key, pass through
    if (!enabled || !siteKey) {
      return null;
    }

    // Check individual form protection
    if (actionName === 'contact' && botConfig?.protect_contact === false) {
      return null;
    }
    if (actionName === 'newsletter' && botConfig?.protect_newsletter === false) {
      return null;
    }

    // Safety timeout helper so users are NEVER locked out or delayed indefinitely
    const withTimeout = (promise, ms = 4500) => {
      return Promise.race([
        promise,
        new Promise(resolve => setTimeout(() => resolve(null), ms))
      ]);
    };

    // 1. CLOUDFLARE TURNSTILE (INVISIBLE)
    if (provider === 'turnstile') {
      return withTimeout(new Promise((resolve) => {
        try {
          if (!window.turnstile) {
            // Script might still be loading or blocked by adblock
            let attempts = 0;
            const interval = setInterval(() => {
              attempts++;
              if (window.turnstile) {
                clearInterval(interval);
                runTurnstile();
              } else if (attempts > 15) {
                clearInterval(interval);
                resolve(null);
              }
            }, 100);
            return;
          }

          runTurnstile();

          function runTurnstile() {
            // Create or reuse hidden container
            let container = document.getElementById('itforsec-turnstile-host');
            if (!container) {
              container = document.createElement('div');
              container.id = 'itforsec-turnstile-host';
              container.style.position = 'fixed';
              container.style.left = '-9999px';
              container.style.top = '-9999px';
              container.style.width = '1px';
              container.style.height = '1px';
              container.style.overflow = 'hidden';
              container.style.opacity = '0';
              container.style.pointerEvents = 'none';
              document.body.appendChild(container);
            }

            try {
              // If previously rendered widget exists, reset it
              if (turnstileWidgetIdRef.current !== null) {
                try {
                  window.turnstile.reset(turnstileWidgetIdRef.current);
                } catch (e) {}
              }

              // Render invisible Turnstile challenge
              const widgetId = window.turnstile.render(container, {
                sitekey: siteKey,
                action: actionName,
                size: 'invisible',
                callback: (token) => {
                  resolve(token);
                },
                'error-callback': () => {
                  resolve(null);
                },
                'expired-callback': () => {
                  resolve(null);
                },
                'timeout-callback': () => {
                  resolve(null);
                }
              });

              turnstileWidgetIdRef.current = widgetId;
            } catch (err) {
              resolve(null);
            }
          }
        } catch (e) {
          resolve(null);
        }
      }));
    }

    // 2. GOOGLE RECAPTCHA V3 (INVISIBLE)
    if (provider === 'recaptcha_v3') {
      return withTimeout(new Promise((resolve) => {
        try {
          if (!window.grecaptcha) {
            let attempts = 0;
            const interval = setInterval(() => {
              attempts++;
              if (window.grecaptcha && window.grecaptcha.ready) {
                clearInterval(interval);
                runRecaptcha();
              } else if (attempts > 15) {
                clearInterval(interval);
                resolve(null);
              }
            }, 100);
            return;
          }

          runRecaptcha();

          function runRecaptcha() {
            window.grecaptcha.ready(() => {
              window.grecaptcha.execute(siteKey, { action: actionName })
                .then(token => resolve(token))
                .catch(() => resolve(null));
            });
          }
        } catch (err) {
          resolve(null);
        }
      }));
    }

    return null;
  }, [enabled, provider, siteKey, botConfig?.protect_contact, botConfig?.protect_newsletter]);

  return {
    executeBotDefense,
    isBotDefenseActive: enabled && !!siteKey,
    provider,
    siteKey
  };
};

export default useBotDefense;
